import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import { PageContainer, Section } from '../components/AdminContentWrapper';
import HelpIcon from '../../../components/help/HelpIcon';
import { DataContext } from '../../../context/DataContext';
import KnowledgeBaseService from '../../../services/KnowledgeBaseService';
import SemanticModelService from '../../../services/SemanticModelService';
import KnowledgeBaseAdminDialog from '../components/KnowledgeBaseAdminDialog';
import TablePaginationBar from '../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../hooks';

const TRANSIENT_STATUSES = new Set(['PENDING', 'PROVISIONING', 'SYNCING', 'DELETING']);
// Techo del polling automático: si una fila lleva más que esto sin cambiar
// de estado, probablemente el worker falló en silencio (ver
// whisperbi-agent-management-strands/knowledge_base_admin.py) -- se deja
// de re-pollear sola y se le avisa al usuario con un botón manual, en vez
// de una rueda de carga eterna. Bien por debajo del Timeout:900 del
// worker, tiempo de sobra para el caso normal.
const STUCK_THRESHOLD_MS = 5 * 60 * 1000;

const isStale = (kb) => {
  const updated = kb?.updated_at ? new Date(kb.updated_at).getTime() : NaN;
  return Number.isFinite(updated) && Date.now() - updated > STUCK_THRESHOLD_MS;
};

const STATUS_META = {
  PENDING: { label: 'Pendiente', color: 'var(--wb-accent)', bg: 'var(--wb-accent-18)' },
  PROVISIONING: { label: 'Provisionando', color: 'var(--wb-accent)', bg: 'var(--wb-accent-18)' },
  SYNCING: { label: 'Sincronizando', color: 'var(--wb-x-text-a78bfa, #a78bfa)', bg: 'var(--wb-x-bg-a78bfa2e, rgba(167, 139, 250, 0.18))' },
  ACTIVE: { label: 'Activa', color: 'var(--wb-success)', bg: 'var(--wb-x-bg-4ade8033, rgba(74, 222, 128, 0.2))' },
  FAILED: { label: 'Error', color: 'var(--wb-danger)', bg: 'var(--wb-x-bg-f8717133, rgba(248, 113, 113, 0.2))' },
  DELETING: { label: 'Eliminando', color: 'var(--wb-warning)', bg: 'var(--wb-x-bg-fbbf242e, rgba(251, 191, 36, 0.18))' }
};

const darkField = { '& .MuiInputBase-root': { backgroundColor: 'var(--wb-bg-surface)', color: 'var(--wb-text-title)' } };

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { label: status || '—', color: 'var(--wb-text-label)', bg: 'var(--wb-border-16)' };
  return <Chip size="small" label={meta.label} sx={{ color: meta.color, backgroundColor: meta.bg, fontWeight: 600 }} />;
};

const KnowledgeBasesOverview = () => {
  const { hasPermission, isPlatformRoot, isTenantAdmin, userData } = useContext(DataContext);
  const canManage = Boolean(
    isPlatformRoot || isTenantAdmin || (typeof hasPermission === 'function' && hasPermission('agents.manage'))
  );
  const accessChecked = Boolean(userData);

  const [items, setItems] = useState([]);
  const [models, setModels] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [form, setForm] = useState({ modelId: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const pollRef = useRef(null);

  const fetchList = useCallback(async () => {
    if (!canManage) return;
    setListLoading(true);
    setListError('');
    try {
      const data = await KnowledgeBaseService.listKnowledgeBases();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      setListError(error?.message || 'No se pudo obtener la lista de bases de conocimiento.');
    } finally {
      setListLoading(false);
    }
  }, [canManage]);

  const fetchModels = useCallback(async () => {
    if (!canManage) return;
    try {
      const res = await SemanticModelService.list();
      const list = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
      setModels(list.filter((m) => !m?.is_deleted));
    } catch (error) {
      console.error('KnowledgeBasesOverview.fetchModels', error);
    }
  }, [canManage]);

  useEffect(() => {
    fetchList();
    fetchModels();
  }, [fetchList, fetchModels]);

  // Polling mientras haya alguna KB en estado transitorio Y todavía dentro
  // del umbral razonable -- una vez "stuck" (ver STUCK_THRESHOLD_MS), deja
  // de contar para el auto-poll (igual sigue en la lista, con un aviso y
  // un botón de actualizar manual, ver más abajo).
  const transientItems = useMemo(() => items.filter((it) => TRANSIENT_STATUSES.has(it.status)), [items]);
  const stuckItems = useMemo(() => transientItems.filter(isStale), [transientItems]);
  const hasTransient = useMemo(() => transientItems.some((it) => !isStale(it)), [transientItems]);
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (hasTransient) {
      pollRef.current = setInterval(fetchList, 8000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasTransient, fetchList]);

  const modelsWithKb = useMemo(
    () => new Set(items.map((it) => it.semantic_model_id)),
    [items]
  );

  const { pageRows, paginationProps } = useTablePagination(items, {
    storageKey: 'knowledgeBases'
  });

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!form.modelId) return;
    setCreating(true);
    setFeedback(null);
    try {
      await KnowledgeBaseService.createKnowledgeBaseFromModel({
        semanticModelId: form.modelId,
        description: form.description || undefined
      });
      setFeedback({ severity: 'success', message: 'Base de conocimiento en cola de creación. El estado se irá actualizando.' });
      setForm({ modelId: '', description: '' });
      await fetchList();
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'No se pudo crear la base de conocimiento.' });
    } finally {
      setCreating(false);
    }
  };

  const handleSync = async (id) => {
    setBusyId(id);
    setFeedback(null);
    try {
      await KnowledgeBaseService.syncKnowledgeBase(id);
      setFeedback({ severity: 'success', message: 'Sincronización iniciada.' });
      await fetchList();
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'No se pudo sincronizar.' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (kb) => {
    if (!window.confirm(`¿Eliminar la base de conocimiento "${kb.name}"?`)) return;
    setBusyId(kb.id);
    setFeedback(null);
    try {
      await KnowledgeBaseService.deleteKnowledgeBase(kb.id);
      setFeedback({ severity: 'success', message: 'Eliminación iniciada.' });
      await fetchList();
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'No se pudo eliminar.' });
    } finally {
      setBusyId(null);
    }
  };

  if (!accessChecked) {
    return (
      <PageContainer title="Bases de conocimiento" description="Administra las bases de conocimiento de tus agentes.">
        <Section title="Cargando acceso">
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Verificando permisos...</Typography>
          </Stack>
        </Section>
      </PageContainer>
    );
  }

  if (!canManage) {
    return (
      <PageContainer title="Bases de conocimiento" description="Solo quien administra agentes puede gestionar las bases de conocimiento.">
        <Section title="Acceso restringido">
          <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
            Necesitas el permiso <strong>agents.manage</strong> (o ser administrador) para acceder a esta sección.
          </Alert>
        </Section>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Bases de conocimiento"
      description="Crea una base de conocimiento desde un modelo semántico, sincronízala cuando cambien tus tablas y ligála a un agente."
      actions={[
        <Button
          key="refresh"
          variant="outlined"
          color="info"
          startIcon={<RefreshIcon />}
          onClick={fetchList}
          disabled={listLoading}
          sx={{ textTransform: 'none', borderColor: 'var(--wb-accent-40)', color: 'var(--wb-accent)' }}
        >
          Actualizar
        </Button>
      ]}
    >
      {feedback && (
        <Box sx={{ mb: 3 }}>
          <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ backgroundColor: 'var(--wb-x-bg-0e749026, rgba(14, 116, 144, 0.15))', color: 'var(--wb-text)' }}>
            {feedback.message}
          </Alert>
        </Box>
      )}

      <Section
        title="Crear base de conocimiento"
        subtitle="Se genera a partir de las tablas del modelo semántico seleccionado. La creación es asíncrona."
      >
        <Box
          component="form"
          onSubmit={handleCreate}
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 160px' } }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <FormControl sx={darkField} required fullWidth>
              <InputLabel>Modelo semántico</InputLabel>
              <Select
                label="Modelo semántico"
                value={form.modelId}
                onChange={(e) => setForm((p) => ({ ...p, modelId: e.target.value }))}
              >
                {models.map((m) => {
                  const id = m.semantic_model_id;
                  const taken = modelsWithKb.has(id);
                  return (
                    <MenuItem key={id} value={id} disabled={taken}>
                      {(m.name || id) + (taken ? ' · ya tiene KB' : '')}
                    </MenuItem>
                  );
                })}
                {models.length === 0 && <MenuItem disabled value="">No hay modelos semánticos</MenuItem>}
              </Select>
            </FormControl>
            <HelpIcon module="agentes" topic="base-conocimiento" anchor="modelo-semántico" sx={{ mt: 1 }} />
          </Stack>
          <TextField
            label="Descripción (opcional)"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            sx={darkField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="agentes" topic="base-conocimiento" anchor="descripción-opcional" />
                </InputAdornment>
              )
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              disabled={!form.modelId || creating}
              sx={{ width: '100%', textTransform: 'none', fontWeight: 600 }}
            >
              {creating ? 'Creando...' : 'Crear'}
            </Button>
          </Box>
        </Box>
      </Section>

      <Section title="Bases de conocimiento" subtitle={`${items.length} base(s) de conocimiento.`}>
        {listError && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}>
            {listError}
          </Alert>
        )}

        {stuckItems.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 2, backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}
            action={
              <Button color="inherit" size="small" onClick={fetchList} disabled={listLoading} sx={{ textTransform: 'none' }}>
                Actualizar estado
              </Button>
            }
          >
            {stuckItems.length === 1
              ? `"${stuckItems[0].name}" está tardando más de lo esperado en terminar (${(STATUS_META[stuckItems[0].status] || {}).label || stuckItems[0].status}).`
              : `${stuckItems.length} bases de conocimiento están tardando más de lo esperado en terminar.`}{' '}
            El proceso puede seguir en curso. Si no se resuelve solo, en unos minutos más va a pasar a "Error"
            automáticamente y vas a poder reintentar la operación desde las acciones de la fila.
          </Alert>
        )}

        <Box sx={{ position: 'relative', overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 900,
              '& th': { color: 'var(--wb-x-text-94a3b8e6, rgba(148, 163, 184, 0.9))', fontWeight: 600, borderBottomColor: 'var(--wb-border)' },
              '& td': { color: 'var(--wb-text)', borderBottomColor: 'var(--wb-border-06)' }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Modelo semántico</TableCell>
                <TableCell>
                  Estado
                  <HelpIcon module="agentes" topic="base-conocimiento" anchor="estado" sx={{ ml: 0.5 }} />
                </TableCell>
                <TableCell>Documentos</TableCell>
                <TableCell>Última sincronización</TableCell>
                <TableCell>Agentes</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && !listLoading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography sx={{ color: 'var(--wb-muted-70)', textAlign: 'center', py: 3 }}>
                      Todavía no hay bases de conocimiento.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {pageRows.map((kb) => {
                const busy = busyId === kb.id || TRANSIENT_STATUSES.has(kb.status);
                return (
                  <TableRow key={kb.id}>
                    <TableCell>
                      {kb.name}
                      {kb.status === 'FAILED' && kb.status_detail && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--wb-danger-light)' }}>
                          {kb.status_detail}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{kb.semantic_model_name || kb.semantic_model_id}</TableCell>
                    <TableCell><StatusChip status={kb.status} /></TableCell>
                    <TableCell>{kb.document_count ?? '—'}</TableCell>
                    <TableCell>{formatDateTime(kb.last_ingestion?.finished_at || kb.last_ingestion?.started_at)}</TableCell>
                    <TableCell>{(kb.linked_agents || []).length}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Ver detalle">
                          <span>
                            <IconButton size="small" onClick={() => setDetailId(kb.id)} sx={{ color: 'var(--wb-accent)' }}>
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={kb.status === 'FAILED' ? 'Reintentar' : 'Sincronizar (re-ingerir el modelo)'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleSync(kb.id)}
                              disabled={busy || !['ACTIVE', 'FAILED'].includes(kb.status)}
                              sx={{ color: 'var(--wb-x-text-a78bfa, #a78bfa)' }}
                            >
                              {busyId === kb.id ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(kb)}
                              disabled={busyId === kb.id || kb.status === 'DELETING'}
                              sx={{ color: 'var(--wb-danger)' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {items.length > 0 && <TablePaginationBar {...paginationProps} />}

          {listLoading && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ position: 'absolute', inset: 0, backgroundColor: 'var(--wb-bg-overlay-70)' }}
            >
              <CircularProgress size={24} />
              <Typography sx={{ color: 'var(--wb-text-label)' }}>Actualizando...</Typography>
            </Stack>
          )}
        </Box>
      </Section>

      <KnowledgeBaseAdminDialog
        open={Boolean(detailId)}
        knowledgeBaseId={detailId}
        onClose={() => setDetailId(null)}
      />
    </PageContainer>
  );
};

export default KnowledgeBasesOverview;
